import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { memo, useCallback } from "react";
import {
  DateInput,
  FilePicker,
  FormContainer,
  FormInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { IDigitalCertificateFileProps } from "./models";
import {
  digitalCertificateFileSchema,
  TDigitalCertificateFileForm,
} from "./digitalCertificateFileSchema";
import {
  createDigitalCertificateFile,
  getDigitalCertificateFileById,
  updateDigitalCertificateFileById,
} from "../../../../../../services/digitalCertificateFile.service";
import {
  dateOnlyStringToNewDate,
  fileToRawBase64,
  rawBase64ToFile,
} from "../../../../../../utils/conversions";

const translationPath =
  "page.register.digital_certificates.digitalCertificateFile.";

export const DigitalCertificateFile = memo(
  ({
    id,
    companyDigitalCertificateId,
    onBackButton,
  }: IDigitalCertificateFileProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createDigitalCertificateFile,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateDigitalCertificateFileById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getDigitalCertificateFileById,
    );

    const saveHandler = async (v: TDigitalCertificateFileForm) => {
      const fileContent = await fileToRawBase64(v.fileContent);

      return save({
        ...v,
        companyDigitalCertificateId: companyDigitalCertificateId ?? "",
        fileContent: fileContent,
        initialDate: v.initialDate.toISOString().substring(0, 10),
        endDate: v.endDate.toISOString().substring(0, 10),
      });
    };

    const updateHandler = async (
      v: TDigitalCertificateFileForm,
      id: string,
    ) => {
      const fileContent = await fileToRawBase64(v.fileContent);

      return update({
        item: {
          ...v,
          companyDigitalCertificateId: companyDigitalCertificateId ?? "",
          fileContent: fileContent,
          initialDate: v.initialDate.toISOString().substring(0, 10),
          endDate: v.endDate.toISOString().substring(0, 10),
        },
        id: id,
      });
    };

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TDigitalCertificateFileForm>> => {
        const { data, success } = await get(id);
        if (data && success) {
          const fileContent = await rawBase64ToFile(
            data.fileContent,
            "",
            "application/x-pkcs12",
          );

          const newData: TDigitalCertificateFileForm = {
            ...data,
            initialDate: dateOnlyStringToNewDate(data.initialDate),
            endDate: dateOnlyStringToNewDate(data.endDate),
            fileContent: fileContent,
          };
          return { data: newData, success: true };
        }

        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TDigitalCertificateFileForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        propId={id}
        loading={loading}
        title={t(translationPath + "digital_certificate_file")}
        schema={digitalCertificateFileSchema}
        subtitle={t(translationPath + "new_digital_certificate_file")}
        isModalForm={true}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DateInput
                name="initialDate"
                label={t(translationPath + "initial_date")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInput
                name="endDate"
                label={t(translationPath + "end_date")}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                size="small"
                name="filePassword"
                fullWidth
                type="password"
                label={t(translationPath + "password")}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mx: 3 }}
              >
                {t(translationPath + "password_caption")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FilePicker
                name="fileContent"
                label={`${t(translationPath + "file")}`}
                maxSize={18}
                accept="x-pkcs12"
              />
            </Grid>
          </Grid>
        )}
      />
    );
  },
);
